import React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { ja } from "date-fns/locale/ja";
import { IconQuestCalendarMonth } from "assets/svg";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ja", ja);

export const inputClassName =
  "h-[50px] w-full rounded-[6px] border border-[#d5d5d5] bg-white px-[15px] text-[16px] leading-[1.6] text-black outline-none placeholder:text-[#adbbcc] focus:border-[#1b4b86]";

export const selectClassName =
  "h-[50px] w-full appearance-none rounded-[6px] border border-[#d5d5d5] bg-white px-[15px] text-[16px] leading-[1.6] text-black outline-none focus:border-[#1b4b86]";

export const FieldLabel = ({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) => (
  <label className="text-[16px] font-medium leading-[1.6] text-black">
    {children}
    {required ? <span className="text-[#de3c3f]"> *</span> : null}
  </label>
);

export const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <p className="text-[13px] leading-[1.6] text-[#de3c3f]" role="alert">
      {message}
    </p>
  ) : null;

interface QuestFormFieldProps {
  label: string;
  children: React.ReactNode;
  className?: string;
  required?: boolean;
}

export const QuestFormField = ({
  label,
  children,
  className = "",
  required = false,
}: QuestFormFieldProps) => (
  <div className={`flex flex-col gap-[10px] ${className}`}>
    <FieldLabel required={required}>{label}</FieldLabel>
    {children}
  </div>
);

export const MonthField = ({
  disabled = false,
  name,
  onBlur,
  onChange,
  placeholder,
  value,
}: {
  disabled?: boolean;
  name: string;
  onBlur: () => void;
  onChange: (value: string) => void;
  placeholder: string;
  value: string;
}) => {
  const selectedDate = parseMonthValue(value);

  return (
    <DatePicker
      calendarIconClassName="!left-auto !right-[15px] !top-1/2 !h-6 !w-6 !-translate-y-1/2 !p-0"
      className={`${inputClassName} !px-[15px] !pr-[50px] cursor-pointer disabled:cursor-not-allowed disabled:bg-[#f5f5f5] disabled:text-[#adbbcc]`}
      dateFormat="yyyy年M月"
      disabled={disabled}
      icon={<IconQuestCalendarMonth className="h-6 w-6" />}
      locale="ja"
      name={name}
      onBlur={onBlur}
      onChange={(date: Date | null) => onChange(formatMonthValue(date))}
      placeholderText={placeholder}
      popperClassName="z-[100]"
      popperProps={{ strategy: "fixed" }}
      selected={selectedDate}
      showMonthYearPicker
      showIcon
      toggleCalendarOnIconClick
      wrapperClassName="w-full"
    />
  );
};

const parseMonthValue = (value: string) => {
  if (!value) return null;

  const [year, month] = value.split("-").map(Number);

  if (!year || !month) return null;

  return new Date(year, month - 1, 1);
};

const formatMonthValue = (date: Date | null) => {
  if (!date) return "";

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}`;
};
